import React, { useEffect, useState } from "react";
import { Joyride, STATUS } from "react-joyride";
import { useLocation, useNavigate } from "react-router-dom";
import { size as floatingSize } from "@floating-ui/dom";
import { api } from "../lib/api";

/**
 * Guided platform tour. Auto-starts on a member's first visit and can be
 * re-triggered from /walkthrough. Steps use data-testids that already exist
 * across the app so the tour survives layout changes.
 *
 * Coordinated via window events:
 *   window.dispatchEvent(new Event("cpg:tour:start"))  — start from anywhere
 *   window.dispatchEvent(new Event("cpg:tour:reset"))  — clear the seen flag
 */
export const TOUR_SEEN_KEY = "cpgTourSeen";
export const VICTORY_SEEN_KEY = "cpgVictorySeen";
export const TOUR_OPTOUT_KEY = "cpgTourOptOut";
export const TOUR_PROMPT_SESSION_KEY = "cpgTourPromptDismissed";

// The full sidebar menu tour. Every entry maps to a real sidebar nav
// testid — steps whose target isn't in the DOM (e.g. plan-gated items
// hidden on a Free account) are filtered out at run-time in
// `filterStepsToDom` so the tour never freezes or falls back to a
// mis-placed centered tooltip.
const STEPS = [
  { target: "[data-testid='nav-welcome']",            title: "Welcome, founder.",              content: "Your home base — a personal intro from Warren, a first-week checklist, and quick links into every part of the platform." },
  { target: "[data-testid='nav-walkthrough']",        title: "Platform Walkthrough",           content: "Come back here anytime to re-run any tour or launch a deep-dive of a specific section." },
  { target: "[data-testid='nav-dashboard']",          title: "Dashboard",                      content: "Your daily glance — streak, course progress, submitted work, certificates, and what to open next." },
  { target: "[data-testid='nav-course']",             title: "Course Overview",                content: "The CPG Founder Program — 16 core modules covering everything from company setup to retail scale, each with a video, worksheets, and a quiz." },
  { target: "[data-testid='nav-modules-toggle'],[data-testid='nav-modules']",            title: "Course Modules",                 content: "Jump straight into any specific module. Ideal when you know exactly what you want to work on." },
  { target: "[data-testid='nav-workbook']",           title: "Course Workbook",                content: "48 decision-driven worksheets — pick one, answer 3-5 prompts, and end each with a written decision you can act on." },
  { target: "[data-testid='nav-decisions']",          title: "Your Decisions",                 content: "Every worksheet you submit lives here — a running log of the calls you've made, filterable by module." },
  { target: "[data-testid='nav-progress']",           title: "Progress & Certificates",        content: "Track quiz scores, unlock module badges, and download signed PDF certifications with a public verify URL." },
  { target: "[data-testid='nav-final-exam']",         title: "Final Certification Exam",       content: "50 questions that certify you as a Certified CPG Founder™ once you've completed the core curriculum." },
  { target: "[data-testid='nav-tools-toggle'],[data-testid='nav-tools']",              title: "Financial Tools",                content: "Value chain, retail margin, financial projections, and cap-table calculators — the exact spreadsheets buyers and investors expect." },
  { target: "[data-testid='nav-blueprint']",          title: "Launch Blueprint",               content: "A structured single-page plan for your brand — Warren's team reviews and gives you personal feedback." },
  { target: "[data-testid='nav-planner']",            title: "Marketing Planner",              content: "Plan and budget your 12-month marketing calendar — content, launches, promos, and ROI in one view." },
  { target: "[data-testid='nav-projects']",           title: "My Projects",                    content: "Every saved worksheet, list, and AI-Studio output in one place. Group into folders for launches, retailers, or investors." },
  { target: "[data-testid='nav-ai-center-toggle'],[data-testid='nav-ai-center']",          title: "AI Studio",                      content: "Generative tools for CPG founders — packaging, logo, meta ads, formulation drafts, and more.",             placement: "right" },
  { target: "[data-testid='nav-members']",            title: "Member Directory",               content: "Find other founders in the Institute, save the ones you want to reach out to, and track outreach in a private pipeline." },
  { target: "[data-testid='nav-industry-directory']", title: "Industry Directory",             content: "Verified CPG vendors and partners — brokers, co-mans, agencies, and legal — with references and fraud checks." },
  { target: "[data-testid='nav-deals']",              title: "CPG Deals",                      content: "Founder-only vendor deals — discounts on the tools and services you actually need to run the brand." },
  { target: "[data-testid='nav-databases-toggle'],[data-testid='nav-databases']",          title: "Partner Databases",              content: "8 vetted directories: retailers, buyers, brokers, distributors, and 6,400+ investors ready for outreach." },
  { target: "[data-testid='nav-community-profile']",  title: "Community Profile",              content: "Publish your public founder profile so other members can find you, then decide who's allowed to reach out." },
  { target: "[data-testid='nav-buyer-alerts']",       title: "Buyer Alerts",                   content: "Live buyer signals — RFPs, category expansions, and new SKU windows for the retailers you're targeting." },
  { target: "[data-testid='nav-story-wall']",         title: "Founder Story Wall",             content: "Community wins and lessons from other Institute founders. Post yours and get discovered." },
  { target: "[data-testid='assistant-bubble-toggle']", title: "Morgan — always one tap",    content: "The emerald bubble in the corner opens Morgan on any page. Text mode, read-aloud, or full voice conversation — you pick.", placement: "left" },
  { target: "[data-testid='nav-account']",            title: "Account & Access",               content: "Manage your subscription, notifications, team seats, and security settings." },
  { target: "[data-testid='topbar-avatar']",          title: "Your profile",                   content: "Upload a photo, update your name, and get to Sign out. That's it — happy building!" },
];

// ── Victory tour — auto-runs on /progress the first time a member holds a cert
const VICTORY_STEPS = [
  {
    target: "[data-testid='progress-primary'], [data-testid='progress-heading']",
    title: "Congrats, founder. 🎉",
    content: "You just earned your first CPG Institute certification. Let's turn that into signal.",
    placement: "center",
  },
  {
    target: "[data-testid='cert-download-primary'], [data-testid='progress-badge-rail']",
    title: "Download your certificate",
    content: "A signed PDF is ready to save or print. Every certificate has a public verify URL so anyone can check it's real.",
  },
  {
    target: "[data-testid='cert-linkedin-primary'], [data-testid='linkedin-share']",
    title: "Share the win on LinkedIn",
    content: "Tap 'Share on LinkedIn' and CPG Institute pre-fills the announcement — smart wins get discovered.",
  },
  {
    target: "[data-testid='nav-members']",
    title: "Tell the community",
    content: "Consider adding your win to your Members Directory bio so peers see momentum. Congrats again!",
  },
];

// ── Deep-dive section tours ──────────────────────────────────────────────
// Each section tour navigates to `path` (if not already there), then runs
// its own set of Joyride steps. Missing targets fall back to a centered
// tooltip so the tour never freezes.
export const SECTION_TOURS = {
  dashboard: {
    label: "Dashboard",
    blurb: "Your daily glance — streaks, progress, and what to open first.",
    path: "/dashboard",
    steps: [
      {
        target: "[data-testid='dashboard-heading']",
        title: "Your daily home base",
        content: "The Dashboard is designed for a 30-second morning check-in. Read the greeting, see what's next, and get to work.",
        placement: "bottom",
      },
      {
        target: "[data-testid='streak-chip']",
        title: "Your streak lives here",
        content: "The flame counts consecutive days you've visited. Cross 7 and 30 days and Morgan gives you a shout-out. Miss a day and it resets to 1 — no guilt.",
        placement: "bottom",
      },
      {
        target: "[data-testid='stat-course'], [data-testid='stat-modules']",
        title: "Big numbers, at a glance",
        content: "Course complete, modules complete, saved work, certificates. If the number is 0, you know exactly where the next 5 minutes should go.",
        placement: "bottom",
      },
      {
        target: "[data-testid='continue-card']",
        title: "Continue where you left off",
        content: "Every visit, this card points you to the module you were last inside. One click and you're back in.",
        placement: "top",
      },
      {
        target: "[data-testid='quick-databases']",
        title: "Quick jumps to what you need",
        content: "Databases, financial tools, downloads, and your saved projects — one tap each.",
        placement: "top",
      },
    ],
  },
  welcome: {
    label: "Welcome page",
    blurb: "Founder intro video + first-week checklist.",
    path: "/welcome",
    steps: [
      {
        target: "body",
        title: "Warren's welcome video",
        content: "A short personal note from Warren about how to use the Institute in your first week. Watch once, then treat it as your onboarding compass.",
        placement: "center",
      },
      {
        target: "body",
        title: "First-week checklist",
        content: "Five concrete actions to take in your first seven days. Do these and the rest of the platform makes sense.",
        placement: "center",
      },
    ],
  },
  course: {
    label: "Course Overview",
    blurb: "The CPG Founder Program — 16 modules, each with video, worksheets, and a quiz.",
    path: "/course",
    steps: [
      {
        target: "body",
        title: "The 16-module curriculum",
        content: "Structured in the order a founder actually needs them: company setup → product → brand → sales → scale. Do them in order the first time.",
        placement: "center",
      },
      {
        target: "body",
        title: "Anatomy of a module",
        content: "Each module has a 3/4 video + 1/4 module summary at the top, sticky tabs for Overview / Reading / To-do / Worksheets / Tools / Downloads / Quiz, and a quiz gating the next module.",
        placement: "center",
      },
      {
        target: "body",
        title: "Quizzes unlock progress",
        content: "Score 80% or above on a module quiz to earn its badge. All 16 quizzes unlock the Final Certification Exam.",
        placement: "center",
      },
    ],
  },
  progress: {
    label: "Progress & Certificates",
    blurb: "Track quiz scores, badges, and download signed PDF certificates.",
    path: "/progress",
    steps: [
      {
        target: "[data-testid='progress-heading'], body",
        title: "Every quiz score in one place",
        content: "Your best score per module, most recent attempt, and the badges you've earned. Re-take any quiz to lift a score.",
        placement: "center",
      },
      {
        target: "[data-testid='cert-download-primary'], [data-testid='progress-badge-rail'], body",
        title: "Downloadable certificates",
        content: "Every module you clear gives you a signed PDF certificate with a public verify URL. Save them, print them, share them.",
        placement: "center",
      },
      {
        target: "[data-testid='cert-linkedin-primary'], [data-testid='linkedin-share'], body",
        title: "One-click LinkedIn share",
        content: "Announce a win with a pre-filled LinkedIn post. Wins that get seen get remembered — recruiters, buyers, and investors all check LinkedIn first.",
        placement: "center",
      },
    ],
  },
  exam: {
    label: "Final Certification Exam",
    blurb: "50 questions. Pass and you're a Certified CPG Founder™.",
    path: "/exam/final",
    steps: [
      {
        // The Certified CPG Founder™ hero card at the top of the live exam
        // landing. Targets a real on-page element so the tour anchors
        // visually to the page rather than dumping a center-of-screen modal.
        target: "[data-testid='final-landing-hero']",
        title: "The Certified CPG Founder™ exam",
        content: "Welcome to the gateway to your credential. 50 questions covering every module — answer 80% correctly and you're a Certified CPG Founder™.",
        placement: "bottom",
      },
      {
        target: "[data-testid='final-bottom-line']",
        title: "What you need to pass",
        content: "You need to pass each of the 10 core module quizzes first. Once those are green, this card unlocks the exam itself.",
        placement: "top",
      },
      {
        // On a founder who has completed modules, the "ready" status card
        // + Take Exam CTA is on-page. On a founder who's locked, this
        // target is absent — filterStepsToDomAsync will quietly drop the
        // step without breaking the tour.
        target: "[data-testid='final-take-exam-cta']",
        title: "Starting the exam",
        content: "When you're ready, this is where you launch it. Unlimited attempts, but the exam is cumulative — budget 60-90 minutes.",
        placement: "bottom",
      },
    ],
  },
  blueprint: {
    label: "Launch Blueprint",
    blurb: "Structured plan for your brand — Warren's team gives feedback.",
    path: "/blueprint",
    steps: [
      {
        target: "body",
        title: "Your brand, on one page",
        content: "The Launch Blueprint walks you through the exact plan investors and retailers expect to see: positioning, product, pricing, pipeline, and 12-month plan.",
        placement: "center",
      },
      {
        target: "body",
        title: "Warren's team reviews it",
        content: "Submit your draft and Warren's team reads it personally and sends targeted feedback. This is the fastest way to sanity-check your plan.",
        placement: "center",
      },
    ],
  },
  assistant: {
    label: "Morgan",
    blurb: "Course-aware chat + ElevenLabs voice mode, on every page.",
    path: "/assistant",
    steps: [
      {
        target: "body",
        title: "Ask Morgan anything",
        content: "Trained on the full course + your progress. Ask it about positioning, pricing, buyers, distributors, forecasting — anything a CPG founder actually asks.",
        placement: "center",
      },
      {
        target: "[data-testid='assistant-bubble-toggle']",
        title: "Bubble on every page",
        content: "The emerald bubble in the corner opens Morgan on any page. Text mode, read-aloud, or full voice conversation — you pick.",
        placement: "left",
      },
      {
        target: "body",
        title: "Voice mode",
        content: "The mic icon starts a real-time conversation via ElevenLabs. Great for pacing around and thinking out loud with Morgan as a sounding board.",
        placement: "center",
      },
    ],
  },
  members: {
    label: "Members Directory",
    blurb: "Find founders, save them, and track outreach in your pipeline.",
    path: "/members",
    steps: [
      {
        target: "body",
        title: "Every founder in the Institute",
        content: "Browse or search fellow members by brand, city, category, and stage. Every profile shows what they'll respond to and how to reach them.",
        placement: "center",
      },
      {
        target: "body",
        title: "Bookmark the ones you want",
        content: "Star a founder to save them to your private pipeline. Add a note, set a reminder date, and mark the stage (New, Reached out, Chatted, In progress).",
        placement: "center",
      },
      {
        target: "body",
        title: "Follow-up reminders",
        content: "Set a reminder-in-days on any bookmark. When it's due, we surface it on your Dashboard so nobody slips through the cracks.",
        placement: "center",
      },
    ],
  },
  databases: {
    label: "Partner Databases",
    blurb: "8 vetted directories — 6,400+ rows of buyers, brokers, and investors.",
    path: "/databases",
    steps: [
      {
        target: "body",
        title: "8 curated databases",
        content: "Retailers, buyers, brokers, distributors, co-manufacturers, ingredients, investors, and press. Not scraped — hand-curated by the Warren team.",
        placement: "center",
      },
      {
        target: "body",
        title: "Filter, sort, export",
        content: "Every table has column-level filters and sorting. Full CSV export is one click — you own the data, take it into your CRM.",
        placement: "center",
      },
      {
        target: "body",
        title: "Save any row to a project",
        content: "Star a contact and it lands in My Projects, tagged and ready for outreach. Combine with Warren AI to draft the first email.",
        placement: "center",
      },
    ],
  },
  tools: {
    label: "Financial Tools",
    blurb: "Price, margin, and value-chain planners for your product.",
    path: "/tools",
    steps: [
      {
        target: "[data-testid='tools-intro']",
        title: "Know your numbers",
        content: "Price-and-margin planners, value-chain visualisers, and other quick calculators that tell you if the unit economics work before you scale.",
        placement: "bottom",
      },
      {
        target: "[data-testid='tools-intro-cta']",
        title: "Skip to the good stuff",
        content: "Jump straight into the tool grid below.",
        placement: "bottom",
      },
      {
        target: "#tools-grid",
        title: "Pick your tool",
        content: "Each tile is a focused calculator — price-point, landed-cost, distributor margin, and more.",
        placement: "top",
      },
      {
        target: "[data-testid='tool-open-value-chain']",
        title: "Crunch the numbers",
        content: "Open any tool to see who takes what percentage of your MSRP and where your margins are actually broken.",
        placement: "top",
      },
    ],
  },
  // CPG Agreements — custom section, shipped as a hardcoded default so the
  // tour works in any environment (preview and production both). The admin
  // console will seed from this when no DB override exists, and admin edits
  // still take precedence via `resolveSectionTour`.
  agreements: {
    label: "CPG Agreements",
    blurb: "Every agreement a CPG brand signs — explained before you sign it.",
    path: "/agreements",
    steps: [
      {
        target: "[data-testid='agreements-cta-intake']",
        title: "Start with your brand",
        content: "Answer a few questions about your business and we'll help identify the documents that may apply to you.",
        placement: "bottom",
      },
      {
        target: "[data-testid='agreements-cta-library']",
        title: "Want to explore instead?",
        content: "Browse the full agreement library and see what's available.",
        placement: "bottom",
      },
      {
        target: "[data-testid='agreements-plan-open']",
        title: "See your full plan",
        content: "Open your personalized plan to see which documents you may need and what to focus on next.",
        placement: "bottom",
      },
      {
        target: "[data-testid='agreements-plan-edit']",
        title: "Something changed?",
        content: "Update your answers anytime and your document plan will adjust with you.",
        placement: "bottom",
      },
      {
        target: "[data-testid='agreements-roadmap-checklist']",
        title: "Want a bigger picture?",
        content: "Use the Roadmap to see what comes next, or the Checklist to keep track of what you've handled.",
        placement: "top",
      },
    ],
  },
  // AI CPG Tools — custom section, shipped as hardcoded default so the tour
  // works in any environment. Each step points at a stable data-testid on
  // AILabDashboard.jsx; admin edits still override via DB.
  "ai-cpg-tools": {
    label: "AI CPG Tools",
    blurb: "Use AI-powered tools to make everyday CPG work faster, easier, and more manageable.",
    path: "/ai-cpg-tools",
    steps: [
      {
        target: "[data-testid='ai-cpg-tools-hero']",
        title: "AI CPG Tools",
        content: "Explore AI tools built for CPG. Choose what you want to create and get prompts designed for your brand and the task at hand.",
        placement: "bottom",
      },
      {
        target: "[data-testid='ai-cpg-tools-brand-profile-banner']",
        title: "Complete your Brand Profile",
        content: "Complete your Brand Profile so the tools can use your brand details to create more relevant prompts and outputs.",
        placement: "bottom",
      },
      {
        target: "[data-testid='ai-cpg-tools-prompt-builder-tile']",
        title: "Build a custom prompt",
        content: "Choose a task, answer a few simple questions, and get a polished prompt you can use with your preferred AI tool.",
        placement: "right",
      },
      {
        target: "[data-testid='ai-cpg-tools-categories-section']",
        title: "Explore specialized AI tools",
        content: "Choose a specialized tool for packaging, branding, advertising, retail, finance, research, operations, and more.",
        placement: "top",
      },
      {
        target: "[data-testid='ai-cpg-tools-formulation-section']",
        title: "Create a formulation brief",
        content: "Build a co-manufacturer-ready formulation brief with ingredient guidance, targets, claims, benchmarks, and regulatory considerations.",
        placement: "top",
      },
      {
        target: "[data-testid='ai-cpg-tools-tools-directory-tile']",
        title: "Find recommended AI tools",
        content: "Not sure which AI platform to use? Explore recommended tools organized by the job you need to get done.",
        placement: "top",
      },
      {
        target: "[data-testid='ai-cpg-tools-popular-section']",
        title: "Start with a popular prompt",
        content: "Need a quick starting point? Choose a ready-made prompt for common CPG tasks and customize it for your brand.",
        placement: "top",
      },
    ],
  },
  planner: {
    label: "Marketing Planner",
    blurb: "Turn a launch plan into dated tasks tied to a channel.",
    path: "/planner",
    steps: [
      {
        target: "body",
        title: "Launch calendar, not a checklist",
        content: "Every marketing task is dated and channelled (email, social, PR, retail push). Reality-check your plan by seeing the weight of each week.",
        placement: "center",
      },
      {
        target: "body",
        title: "Autosaved per user",
        content: "Everything you add is saved to your account, so you can plan on a laptop and check it from your phone at a store visit.",
        placement: "center",
      },
    ],
  },
  downloads: {
    label: "Downloads Library",
    blurb: "Templates, decks, financial models, and worksheets.",
    path: "/downloads",
    steps: [
      {
        target: "body",
        title: "Every template Warren uses",
        content: "P&L models, pitch decks, buyer email templates, one-pagers, and worksheet PDFs. Filtered by module so you can grab what you need.",
        placement: "center",
      },
      {
        target: "body",
        title: "One-click download",
        content: "Tap the icon on any row to download the file directly to your device. Everything stays in your Downloads for offline use.",
        placement: "center",
      },
    ],
  },
  account: {
    label: "Your Account",
    blurb: "Profile, photo, community bio, team seats, and billing.",
    path: "/account",
    steps: [
      {
        target: "[data-testid='topbar-avatar'], body",
        title: "Where your identity lives",
        content: "Your avatar in the top-right opens Account. Upload a real photo — profiles with photos get 3x more community responses.",
        placement: "bottom",
      },
      {
        target: "body",
        title: "Publish your community profile",
        content: "By default your profile is private. Toggle Publish to show up in the Members Directory — brand, city, category, stage, contact links.",
        placement: "center",
      },
      {
        target: "body",
        title: "Team seats",
        content: "On Full Access you can invite up to 3 teammates. Everything they do is under your subscription.",
        placement: "center",
      },
    ],
  },
  admin: {
    label: "Admin Console",
    blurb: "Manage users, plans, content, walkthroughs, and analytics.",
    path: "/admin",
    steps: [
      {
        target: "[data-testid='admin-hero'], body",
        title: "The Admin Console",
        content: "Warren team headquarters — manage members, plans, content, and every operational surface from a single grid.",
        placement: "center",
      },
      {
        target: "[data-testid='admin-menu-grid']",
        title: "Pick a section",
        content: "Every admin surface lives in the grid — Users, Plans, Downloads, Modules, Stories, Blueprints, and Content CMS. Tap a tile to open it below.",
        placement: "bottom",
      },
      {
        target: "[data-testid='admin-tab-users'], [data-testid='admin-menu-grid']",
        title: "Users tab",
        content: "Search founders, change their plan, or delete a test account. Deleting a paying user auto-cancels their Stripe subscription.",
        placement: "bottom",
      },
      {
        target: "[data-testid='admin-tab-cms'], [data-testid='admin-menu-grid']",
        title: "Content CMS",
        content: "Edit every headline, intro block, and walkthrough step live — no redeploy. Changes appear the next time a member loads the page.",
        placement: "bottom",
      },
    ],
  },
};

// LocalStorage key that tracks which section tours a founder has finished.
// Stored as JSON array, e.g. `["dashboard","course"]`.
export const SECTION_TOURS_DONE_KEY = "cpgSectionToursDone";


// ── Admin-editable overrides ─────────────────────────────────────────────
// Cache of tour overrides fetched from `/api/walkthroughs`. Any section id
// present here wins over the hardcoded SECTION_TOURS default above, so admins
// can rewrite step copy / retarget selectors / re-order steps from the Admin
// console without a redeploy.
//
// The endpoint returns a wrapped shape:
//   { overrides: { section_id: row, ... }, hidden: [section_id, ...],
//     order:     [section_id, ...] }
// `hidden` is the source of truth for what the admin marked Hide/Delete —
// we drop those ids from BOTH the merged section-tour map AND the runtime
// tour, so hardcoded defaults don't leak past an admin's Hide action.
let _tourOverridesCache = null;   // { section_id: row }
let _tourHiddenCache    = null;   // Set<section_id>
let _tourOrderCache     = null;   // [section_id, ...] (admin drag order)
let _tourOverridesPromise = null;

async function loadTourOverrides(force = false) {
  if (_tourOverridesCache && !force) return _tourOverridesCache;
  if (_tourOverridesPromise) return _tourOverridesPromise;
  _tourOverridesPromise = api.get("/walkthroughs")
    .then((r) => {
      const data = r.data || {};
      // Wrapped shape: { overrides, hidden, order }
      // Legacy shape: flat dict { section_id: row } — treat as overrides
      // with no hidden set. Detect by checking for the wrapper keys.
      if (data && typeof data === "object"
          && (Array.isArray(data.hidden) || data.overrides)) {
        _tourOverridesCache = data.overrides || {};
        _tourHiddenCache    = new Set(data.hidden || []);
        _tourOrderCache     = Array.isArray(data.order) ? data.order.slice() : [];
      } else {
        _tourOverridesCache = data || {};
        _tourHiddenCache    = new Set();
        _tourOrderCache     = [];
      }
      return _tourOverridesCache;
    })
    .catch(() => {
      _tourOverridesCache = {};
      _tourHiddenCache    = new Set();
      _tourOrderCache     = [];
      return {};
    })
    .finally(() => { _tourOverridesPromise = null; });
  return _tourOverridesPromise;
}

// Exposed for the /walkthrough page so it can render admin-created customs
// AND respect the admin's drag-drop ordering. Reads the module-level caches
// populated by `loadTourOverrides`; safe to call after `useSectionTours`
// resolves (it'll be [] before then).
export function getTourSectionOrder() {
  return _tourOrderCache ? _tourOrderCache.slice() : [];
}
export function isTourSectionHidden(sectionId) {
  return !!(_tourHiddenCache && _tourHiddenCache.has(sectionId));
}

/**
 * Resolve a section tour by id, preferring the DB override over the hardcoded
 * default. Called by both GuidedTour (runtime) and the /walkthrough page.
 * Returns `null` if the section id doesn't exist in either place, OR if
 * the admin has hidden/deleted it (Show/Hide/Delete lifecycle).
 */
export function resolveSectionTour(sectionId) {
  if (_tourHiddenCache?.has(sectionId)) return null;
  const override = _tourOverridesCache?.[sectionId];
  const base = SECTION_TOURS[sectionId];
  if (!override && !base) return null;
  if (!override) return base;
  // Merge: override wins on top-level fields; steps come entirely from override
  // (an admin-cleared step list is intentional — no default fallback).
  return {
    label: override.label || base?.label,
    blurb: override.blurb || base?.blurb,
    path:  override.path  || base?.path,
    steps: (override.steps || []).map((s) => ({
      target: s.target, title: s.title, content: s.content,
      placement: s.placement || base?.steps?.find?.((b) => b.target === s.target)?.placement || "bottom",
    })),
  };
}

/** Hook — returns the merged map of section tour metadata (label/blurb/path
 *  + step count) so the Walkthrough page can render its section grid without
 *  caring whether each entry came from code or DB. Hidden/deleted sections
 *  are dropped so admin Show/Hide/Delete stays in sync with the grid.
 *
 *  Freshness contract:
 *    • Every mount forces a network revalidation. The module-level cache
 *      is only used as an OPTIMISTIC first paint — so navigating to
 *      /walkthrough right after an admin Hide/Delete on another page
 *      never shows a stale grid.
 *    • `cpg:tour:overrides-refreshed` (same-tab admin actions) triggers
 *      an immediate force refresh.
 *    • Tab visibility change → visible, and window focus events also
 *      force a refresh so returning from an admin tab picks up their
 *      edits without a manual reload.
 */
export function useSectionTours() {
  const [tours, setTours] = useState(() => {
    // Optimistic first paint from cache — replaced by the forced
    // refetch below within one tick, so a stale first-frame is the
    // worst case, not a stale grid.
    const merged = {};
    const keys = new Set([
      ...Object.keys(SECTION_TOURS),
      ...Object.keys(_tourOverridesCache || {}),
    ]);
    for (const id of keys) {
      if (_tourHiddenCache?.has(id)) continue;
      const resolved = resolveSectionTour(id);
      if (resolved) merged[id] = resolved;
    }
    // Empty seed on very first render (cache never populated).
    if (Object.keys(merged).length === 0) {
      for (const [id, t] of Object.entries(SECTION_TOURS)) merged[id] = { ...t };
    }
    return merged;
  });
  useEffect(() => {
    let alive = true;
    const rebuild = () => {
      const merged = {};
      const keys = new Set([
        ...Object.keys(SECTION_TOURS),
        ...Object.keys(_tourOverridesCache || {}),
      ]);
      for (const id of keys) {
        if (_tourHiddenCache?.has(id)) continue;
        const resolved = resolveSectionTour(id);
        if (resolved) merged[id] = resolved;
      }
      if (alive) setTours(merged);
    };
    // ALWAYS force-fetch on mount — the module cache may hold stale
    // data from a previous page (e.g. admin just hid a section).
    loadTourOverrides(true).then(rebuild);
    const onRefresh = () => loadTourOverrides(true).then(rebuild);
    const onVisible = () => {
      if (document.visibilityState === "visible") onRefresh();
    };
    window.addEventListener("cpg:tour:overrides-refreshed", onRefresh);
    window.addEventListener("focus", onRefresh);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      alive = false;
      window.removeEventListener("cpg:tour:overrides-refreshed", onRefresh);
      window.removeEventListener("focus", onRefresh);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, []);
  return tours;
}

// ── Runtime step sanitizer ────────────────────────────────────────
// Two failure modes we guard against here:
//   1) A step's `target` selector isn't in the DOM (nav item hidden by
//      plan, a page still painting, a deprecated testid). Joyride's
//      fallback is to render the tooltip centered — which visually
//      reads as "the popup is in the wrong place". Dropping the step
//      is a better UX than confusing centering.
//   2) A step declares a real target but no placement — Joyride's
//      default "bottom" can push a sidebar tooltip off-screen on
//      short viewports. We force `placement: "auto"` for those so
//      Joyride picks the side with the most room.
// A step whose target is literally "body" or "center" is treated as
// intentional and left alone (used for section-wide tooltips).
// After resolving targets, drop any consecutive step whose element is
// the SAME as, or a DESCENDANT of, the previous step's element. Without
// this, an override like:
//
//   step N   → target `[data-testid=welcome-note]`      title "A note from Warren"
//   step N+1 → target `[data-testid=welcome-note-ask]`  title "Ask Warren AI"
//
// visually reads as "the same card highlighted twice with two different
// names" — the founder sees the outer card get a spotlight, clicks Next,
// and the SAME card gets spotlighted again for the inner button. We keep
// the FIRST step (broader context, better copy for a first-time viewer)
// and drop the nested one. Deep-linking to the inner element is still
// possible by placing it in a non-adjacent step or on a different section.
// Build the Floating UI options every step reuses so `autoPlacement`
// / `flip`, `shift`, and our injected `size` middleware share the
// same viewport boundaries — below the fixed AppShell header, above
// the browser edge. The `size` middleware clamps the tooltip's
// max-width / max-height ONLY when the natural size would exceed
// the room available so short content stays at its natural size
// without a scrollbar; long content gets an internal scrollbar as a
// last resort. Explicit placements (Left / Right / Top / Bottom set
// by the admin) are honoured LITERALLY — flip is disabled — so the
// founder's side choice always wins even in tight spots.
function buildStepFloatingOptions({ respectExplicitPlacement = false } = {}) {
  let headerH = 64;
  try {
    const rootStyle = getComputedStyle(document.documentElement);
    headerH = parseInt(rootStyle.getPropertyValue("--header-h"), 10) || 64;
  } catch { /* SSR / test env fallback */ }
  const SAFE = 16;
  const padding = { top: headerH + SAFE, right: SAFE, bottom: SAFE, left: SAFE };
  return {
    // Shift keeps the tooltip inside the padded viewport regardless
    // of placement. Applied to both auto AND explicit — for explicit
    // placements it slides the tooltip along its axis (e.g. a "left"
    // tooltip that runs past the top gets slid down) WITHOUT
    // switching sides, because flip is disabled below.
    shiftOptions: { padding },
    // Explicit placement (top/bottom/left/right) → hard-disable
    // flip so the founder's side pick is honoured literally, exactly
    // matching the Admin Console pick. Auto placement lets react-
    // joyride use its default autoPlacement() (Floater.tsx line 61).
    ...(respectExplicitPlacement ? { flipOptions: false } : {}),
    // Size middleware — width-only clamp. We deliberately never set
    // max-height / overflow-y here: doing so caused an internal
    // scrollbar on the coaching bubble whenever placement (e.g.
    // "bottom") landed near a viewport edge and Floating UI
    // reported a small `availableHeight`. Instead we let the bubble
    // render at its natural height and rely on `ensureStepFullyVisible`
    // (called on Joyride's `tooltip` lifecycle event) to scroll the
    // PAGE so BOTH the highlighted target and the full tooltip fit
    // in the padded viewport — the founder never sees a scrollbar
    // inside the bubble.
    middleware: [
      floatingSize({
        padding,
        apply({ availableWidth, elements }) {
          const el = elements.floating;
          if (!el) return;
          // Clear any prior clamp before measuring natural width so
          // we don't feed the previous frame's constraint back in.
          el.style.maxWidth = "";
          el.style.maxHeight = "";
          el.style.overflowY = "";
          el.style.overflowX = "";
          // Width clamp — only if the tooltip would exceed the
          // padded viewport width. Height is intentionally left
          // untouched so no scrollbar ever appears.
          if (el.scrollWidth > availableWidth) {
            el.style.maxWidth = `${Math.max(240, Math.floor(availableWidth))}px`;
          }
        },
      }),
    ],
  };
}

// After resolving targets, drop only IMMEDIATE consecutive EXACT
// duplicates (same DOM element back-to-back). Previously we also
// collapsed nested descendants — a step whose element sat INSIDE
// the previous step's element — but that silently swallowed intent
// when an admin configured a page-level overview step followed by
// panel-specific drill-in steps.
//
// The account walkthrough had 8 steps in the admin console (step 0
// targeted `account-page`, steps 1-7 targeted individual panels
// inside it) but only 1 was surfacing because every panel is
// a descendant of `account-page` and the old rule swallowed all of
// them. Admin intent wins: only true consecutive duplicates
// (identical DOM node) collapse.
function dedupeConsecutiveNestedSteps(resolvedSteps) {
  const out = [];
  let prevEl = null;
  for (const s of resolvedSteps) {
    let el = null;
    const tgt = (s.target || "").trim();
    if (tgt && tgt !== "body" && tgt !== "center") {
      try { el = document.querySelector(tgt); } catch { /* noop */ }
    }
    if (el && prevEl && el === prevEl) {
      // Exact duplicate — skip. Advance prevEl anyway so a run of
      // duplicates all collapse into the first.
      prevEl = el;
      continue;
    }
    out.push(s);
    prevEl = el;
  }
  return out;
}

function filterStepsToDom(steps) {
  const out = [];
  for (const s of (steps || [])) {
    const tgt = (s.target || "").trim();
    // Intentional whole-page steps → keep as-is.
    if (!tgt || tgt === "body" || tgt === "center") { out.push(s); continue; }
    // Comma-separated targets — Joyride picks the first that matches,
    // so we only need one hit to consider the step valid.
    let found = null;
    for (const sel of tgt.split(",").map((x) => x.trim()).filter(Boolean)) {
      try {
        if (document.querySelector(sel)) { found = sel; break; }
      } catch { /* invalid selector — skip */ }
    }
    if (!found) continue;   // drop the step; the tour advances cleanly
    // Some admin-authored steps target a small CTA button inside a
    // larger card (e.g. "Book a call"). Joyride's dark overlay would
    // then cover the surrounding copy and make the card look clipped/
    // misaligned. Remap those to the parent card so the spotlight
    // illuminates the whole surface.
    const remapped = SPOTLIGHT_PARENT_MAP[found];
    if (remapped) {
      try { if (document.querySelector(remapped)) found = remapped; } catch {}
    }
    // Every step gets the same header-aware Floating UI middleware
    // stack (shift + size) so the tooltip is always fully visible
    // regardless of the founder's placement pick. Explicit
    // placements (top/bottom/left/right) still respect the founder's
    // side choice but can now flip when that side would push the
    // tooltip off-screen — no more silent "auto turns into top" on
    // a full-width card.
    const explicit = s.placement && s.placement !== "auto";
    const floatingOptions = buildStepFloatingOptions({ respectExplicitPlacement: explicit });
    out.push({
      ...s,
      target: found,        // pin to the exact selector that resolved
      placement: s.placement || "auto",
      disableBeacon: true,
      floatingOptions,
    });
  }
  return dedupeConsecutiveNestedSteps(out);
}

// Map of testid targets whose spotlight should extend to a parent card
// container. Small buttons inside CTA cards leave the surrounding copy
// under Joyride's dark overlay, which looks like the card content is
// clipped or misaligned. Remapping the target to the parent card keeps
// the copy readable while the tooltip still points at the same action.
const SPOTLIGHT_PARENT_MAP = {
  "[data-testid='welcome-book-call']": "[data-testid='welcome-call-cta']",
};

// Async DOM-readiness variant. Polls up to `timeoutMs` for each step's
// target so async-mounted components (WeeklyDigestCard, module lists,
// stats fetched over the network, etc.) don't cause their steps to be
// silently dropped when the tour starts before the page finishes
// painting. Body/center steps resolve immediately; comma-separated
// selectors resolve the moment ANY option is present.
//
// Runs ALL step probes IN PARALLEL — the previous serial loop cost
// ~2.5s × N steps and was the root cause of the "10+ second lag
// before the coaching bubble appears" report on `/walkthrough`. With
// parallel probes total wait time is capped at ~timeoutMs regardless
// of step count.
async function filterStepsToDomAsync(steps, timeoutMs = 2500) {
  const probeStep = async (s) => {
    const tgt = (s.target || "").trim();
    if (!tgt || tgt === "body" || tgt === "center") return s;
    const selectors = tgt.split(",").map((x) => x.trim()).filter(Boolean);
    const start = Date.now();
    let found = null;
    // Fast path — check once before the polling loop so already-
    // painted targets skip the 120ms tick entirely.
    for (const sel of selectors) {
      try { if (document.querySelector(sel)) { found = sel; break; } }
      catch { /* invalid selector — ignore */ }
    }
    while (!found && Date.now() - start < timeoutMs) {
      await new Promise((r) => setTimeout(r, 120));
      for (const sel of selectors) {
        try { if (document.querySelector(sel)) { found = sel; break; } }
        catch { /* ignore */ }
      }
    }
    if (!found) return null;    // step is dropped downstream
    // Same parent-remap as the sync variant — keep the whole CTA
    // card lit up so admin-picked small-button targets don't dim
    // the surrounding copy under Joyride's overlay.
    const remapped = SPOTLIGHT_PARENT_MAP[found];
    if (remapped) {
      try { if (document.querySelector(remapped)) found = remapped; } catch {}
    }
    const explicit = s.placement && s.placement !== "auto";
    const floatingOptions = buildStepFloatingOptions({ respectExplicitPlacement: explicit });
    return {
      ...s,
      target: found,
      placement: s.placement || "auto",
      disableBeacon: true,
      floatingOptions,
    };
  };

  const resolved = await Promise.all((steps || []).map(probeStep));
  // Preserve the original step order and drop any step whose target
  // never resolved. Because probes ran in parallel, missing targets
  // no longer block later steps — a stale testid only costs the
  // parallel wait (one probe's timeout), not the serial sum.
  return dedupeConsecutiveNestedSteps(resolved.filter(Boolean));
}

// Scroll the highlighted element into view for tour steps.
//
// Joyride v3's default step-transition scroll uses `block:"center"`
// which silently ignores CSS `scroll-padding-top`, so the target lands
// behind the fixed AppShell header on any step that requires a scroll.
// Setting `disableScrolling: true` on Joyride SHOULD suppress that but
// react-floater (Joyride's tooltip positioner) also nudges scroll to
// keep the tooltip in view, and browser focus-restoration scrolls the
// primary Next button into view — both fight our scroll.
//
// The reliable fix is a short RAF-based scroll lock: on step:before
// we compute the desired scroll-Y (respecting `scroll-padding-top`),
// then re-force it every animation frame for ~500ms so any competing
// scroller gets overridden. After that the tour idles at our target.
function scrollTargetIntoView(target) {
  if (!target || target === "body" || target === "center") { _lastTargetEl = null; return; }
  const selectors = String(target).split(",").map((x) => x.trim()).filter(Boolean);
  let el = null;
  for (const sel of selectors) {
    try { const found = document.querySelector(sel); if (found) { el = found; break; } }
    catch { /* invalid — skip */ }
  }
  _lastTargetEl = el;
  if (!el) return;

  const compute = () => {
    try {
      const rootStyle = getComputedStyle(document.documentElement);
      const headerH = parseInt(rootStyle.getPropertyValue("--header-h"), 10) || 64;
      const GAP = 24;                              // breathing room below the header
      const viewportH = window.innerHeight;
      const rect = el.getBoundingClientRect();
      const elH = rect.height;
      const usableH = viewportH - headerH - GAP * 2;
      // Short elements center in the usable strip below the header
      // so the tooltip has natural room to render above or below.
      // Tall elements pin at `headerH + GAP` so the heading is readable.
      const desiredTopInView = (elH > 0 && elH < usableH)
        ? headerH + GAP + Math.max(0, (usableH - elH) / 2)
        : headerH + GAP;
      const scroller = document.scrollingElement || document.documentElement;
      const currentScrollY = scroller.scrollTop;
      return { scroller, target: Math.max(0, currentScrollY + rect.top - desiredTopInView) };
    } catch { return null; }
  };

  const first = compute();
  if (!first) return;
  first.scroller.scrollTo({ top: first.target, behavior: "smooth" });

  // Lock: for ~500ms keep re-asserting the desired scroll every frame
  // in case Joyride, floater, or focus-scroll overrides us. `compute`
  // re-reads the element on every tick because a mid-frame layout
  // shift (async-mounted content painting in) could move the target.
  const start = performance.now();
  const tick = () => {
    const now = performance.now();
    if (now - start > 500) return;
    const c = compute();
    if (!c) return;
    const cur = c.scroller.scrollTop;
    // Only re-force if we've drifted meaningfully (avoids fighting
    // the browser's own smooth-scroll animation on the last frames).
    if (Math.abs(cur - c.target) > 8) {
      c.scroller.scrollTo({ top: c.target, behavior: "auto" });
    }
    requestAnimationFrame(tick);
  };
  requestAnimationFrame(tick);
}

// Ensure BOTH the highlighted target and the coaching tooltip are
// fully visible on screen. Runs after Joyride paints the tooltip
// (on the `tooltip` lifecycle event) — it measures the union of
// target + tooltip rects and scrolls just enough to fit both inside
// the usable strip between the fixed AppShell header and the bottom
// of the viewport. If the two together are taller than the usable
// strip, we prioritise showing the TARGET's top edge (so the user
// can see what's being highlighted) and accept that the tooltip's
// bottom may clip — which will only ever happen on very tall targets.
function ensureStepFullyVisible() {
  const compute = () => {
    const tooltip = document.querySelector(".react-joyride__tooltip");
    if (!tooltip) return null;
    // Joyride v3 renders the spotlight as a positioned wrapper — we
    // read the underlying target element via the aria-describedby /
    // spotlight target attribute, but simpler: use the last known
    // target-selector stashed by scrollTargetIntoView above.
    const target = _lastTargetEl && document.contains(_lastTargetEl) ? _lastTargetEl : null;
    const tt = tooltip.getBoundingClientRect();
    const rootStyle = getComputedStyle(document.documentElement);
    const headerH = parseInt(rootStyle.getPropertyValue("--header-h"), 10) || 64;
    const GAP = 16;
    const viewportH = window.innerHeight;
    const topBound = headerH + GAP;
    const botBound = viewportH - GAP;

    let unionTop, unionBot;
    if (target) {
      const gr = target.getBoundingClientRect();
      unionTop = Math.min(tt.top, gr.top);
      unionBot = Math.max(tt.bottom, gr.bottom);
    } else {
      unionTop = tt.top;
      unionBot = tt.bottom;
    }
    const unionH = unionBot - unionTop;
    const usableH = botBound - topBound;
    const scroller = document.scrollingElement || document.documentElement;
    let deltaY = 0;
    if (unionH <= usableH) {
      // Fits — center the union in the usable strip if there's slack.
      const desiredTop = topBound + Math.max(0, (usableH - unionH) / 2);
      deltaY = unionTop - desiredTop;
    } else {
      // Doesn't fit — pin the target's top just below the header so
      // the user can always see what's highlighted; tooltip bottom
      // may extend below the fold (unavoidable on very tall cards).
      if (target) {
        const gr = target.getBoundingClientRect();
        deltaY = gr.top - topBound;
      } else {
        deltaY = tt.top - topBound;
      }
    }
    if (Math.abs(deltaY) < 2) return null;
    return { scroller, target: Math.max(0, scroller.scrollTop + deltaY) };
  };

  const first = compute();
  if (first) first.scroller.scrollTo({ top: first.target, behavior: "smooth" });
  // Short RAF lock so late focus/floater nudges can't undo us.
  const start = performance.now();
  const tick = () => {
    if (performance.now() - start > 400) return;
    const c = compute();
    if (c) c.scroller.scrollTo({ top: c.target, behavior: "auto" });
    requestAnimationFrame(tick);
  };
  requestAnimationFrame(tick);
}

// Stash the resolved target element so `ensureStepFullyVisible` can
// read it after Joyride paints the tooltip — Joyride doesn't expose
// the current target via its callback payload in a stable way, and
// re-parsing the selector on every tick would race with async mounts.
let _lastTargetEl = null;

// Module-level lock — StrictMode double-invokes effects; we need one
// consumption of the sessionStorage handoff to survive the cleanup + rerun.
let __pendingTourConsumed = null;

export default function GuidedTour() {
  const [run, setRun] = useState(false);
  const [activeSteps, setActiveSteps] = useState(STEPS);
  const [tourKey, setTourKey] = useState("platform"); // "platform" | "victory" | "section:<id>"
  const location = useLocation();
  const nav = useNavigate();

  const startPlatform = async () => {
    // Filter out sidebar items that aren't in the DOM (plan-gated, etc.)
    // so the tour never lands on a mis-placed centered fallback.
    const steps = await filterStepsToDomAsync(STEPS, 1500);
    if (!steps.length) return;
    setActiveSteps(steps); setTourKey("platform"); setRun(true);
    // Mark as seen the moment we start so any exit (X, ESC, tab close, refresh)
    // still counts. Founders explicitly re-launch from /walkthrough.
    try { localStorage.setItem(TOUR_SEEN_KEY, "1"); } catch { /* private mode */ }
  };
  const startVictory = async () => {
    const steps = await filterStepsToDomAsync(VICTORY_STEPS, 1500);
    if (!steps.length) return;
    setActiveSteps(steps); setTourKey("victory"); setRun(true);
    try { localStorage.setItem(VICTORY_SEEN_KEY, "1"); } catch { /* private mode */ }
  };
  const startSection = async (sectionId) => {
    // Ensure the CMS-editable overrides are loaded BEFORE resolving the
    // tour. Without this await, a click that races the initial /api/walkthroughs
    // fetch resolves against an empty cache, falls back to a hardcoded
    // SECTION_TOURS entry with empty steps, and the popup silently never
    // appears. This is the "not sync with admin console content" bug.
    try { await loadTourOverrides(); } catch { /* ignore — will use defaults */ }
    const tour = resolveSectionTour(sectionId);
    if (!tour) return;
    // Hard-stop the exam tour when the current user has already passed the
    // final — the destination page renders the completion takeover (no
    // exam cards) and the tour steps target the live-exam elements which
    // aren't on that view. Keeping the completion logic intact; this
    // guard is purely about tour entry.
    if (sectionId === "exam") {
      try {
        const r = await api.get("/exam/final");
        if (r?.data?.completed) return;
      } catch { /* non-blocking — allow tour on probe failure */ }
    }
    const go = async () => {
      // Async DOM-readiness wait — async-mounted components (weekly digest,
      // module lists) need a moment to paint. Without this, steps that
      // point at those elements get silently dropped and the founder sees
      // a truncated tour.
      const steps = await filterStepsToDomAsync(tour.steps);
      if (!steps.length) return;   // skip if none of the targets are on-page
      // Fully reset Joyride state before restarting. Without this, a
      // replay while the previous instance is still `run=true` becomes
      // a no-op — Joyride ignores an unchanged `run` prop. Flipping run
      // false → true (via a microtask) forces a re-mount cycle.
      setRun(false);
      setActiveSteps(steps);
      setTourKey(`section:${sectionId}:${Date.now()}`);
      queueMicrotask(() => setRun(true));
    };
    // Navigate to the section's page first (if we're not already there). Since
    // AppShell (which hosts GuidedTour) remounts on route change, we hand off
    // the pending section id via sessionStorage and let the new GuidedTour
    // instance pick it up in the pathname effect below.
    if (tour.path && location.pathname !== tour.path) {
      try {
        sessionStorage.setItem("cpgPendingSectionTour", sectionId);
      } catch { /* private mode */ }
      nav(tour.path);
    } else {
      setTimeout(() => { go(); }, 200);
    }
  };

  // Kick off the override fetch once on mount so future startSection() calls
  // resolve against the admin-edited copy instead of the hardcoded defaults.
  useEffect(() => { loadTourOverrides(); }, []);

  // NOTE: The platform tour no longer auto-starts on first login. A dedicated
  // <WalkthroughPrompt /> modal on the Dashboard asks the founder first and
  // dispatches `cpg:tour:start` if they accept.

  // Pick up a pending section tour after a cross-page navigation. `startSection`
  // stores the section id in sessionStorage before navigating; the fresh
  // GuidedTour instance on the destination page reads it here.
  useEffect(() => {
    let pending = null;
    try {
      pending = sessionStorage.getItem("cpgPendingSectionTour");
    } catch { /* private mode */ }
    if (!pending && __pendingTourConsumed) pending = __pendingTourConsumed;
    if (!pending) return;
    let cancelled = false;
    let timer = null;
    // Ensure CMS overrides are loaded before resolving — otherwise the
    // resume can race the initial fetch and fall back to the hardcoded
    // (possibly empty-steps) default, and the popup never appears.
    loadTourOverrides().finally(() => {
      if (cancelled) return;
      const tour = resolveSectionTour(pending);
      if (!tour) return;
      if (tour.path && location.pathname !== tour.path) return;
      __pendingTourConsumed = pending;
      try { sessionStorage.removeItem("cpgPendingSectionTour"); } catch {}
      timer = setTimeout(async () => {
        __pendingTourConsumed = null;
        // Async DOM-readiness wait so lazily-mounted target elements
        // are in the tree before we hand the step list to Joyride.
        const steps = await filterStepsToDomAsync(tour.steps);
        if (steps.length === 0) return;
        setRun(false);
        setActiveSteps(steps);
        setTourKey(`section:${pending}:${Date.now()}`);
        queueMicrotask(() => setRun(true));
      }, 400);
    });
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [location.pathname]);

  // Victory tour is user-initiated ONLY. It fires when someone
  // dispatches `cpg:tour:victory` (e.g. from the /walkthrough index
  // page's "Show victory tour" button). We intentionally do NOT
  // auto-open it on /progress — a founder scrolling through their
  // Progress & Certificates page must never see a popup they didn't
  // ask for. `VICTORY_SEEN_KEY` is still cleared inside the explicit
  // event handler below so users can replay the tour on demand.

  // Public triggers
  useEffect(() => {
    const reset = () => {
      try { localStorage.removeItem(TOUR_SEEN_KEY); } catch {}
      startPlatform();
    };
    const startVictoryEvt = () => {
      try { localStorage.removeItem(VICTORY_SEEN_KEY); } catch {}
      if (location.pathname !== "/progress") nav("/progress");
      setTimeout(startVictory, 400);
    };
    const startSectionEvt = (e) => {
      const sectionId = e?.detail?.section;
      if (sectionId) startSection(sectionId);
    };
    window.addEventListener("cpg:tour:start", startPlatform);
    window.addEventListener("cpg:tour:reset", reset);
    window.addEventListener("cpg:tour:victory", startVictoryEvt);
    window.addEventListener("cpg:tour:section", startSectionEvt);
    return () => {
      window.removeEventListener("cpg:tour:start", startPlatform);
      window.removeEventListener("cpg:tour:reset", reset);
      window.removeEventListener("cpg:tour:victory", startVictoryEvt);
      window.removeEventListener("cpg:tour:section", startSectionEvt);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const handleCallback = (d) => {
    const { status, action, type, origin, index, step } = d;

    // ── Analytics — fire-and-forget POSTs; failures never break the tour ─
    // Called from every meaningful Joyride lifecycle event so the admin
    // drop-off editor sees granular per-step data.
    const logEvent = (evtAction) => {
      try {
        const sectionId = tourKey.startsWith("section:") ? tourKey.split(":")[1] : tourKey;
        api.post("/walkthroughs/events", {
          tour_key: tourKey,
          section_id: sectionId,
          step_index: typeof index === "number" ? index : undefined,
          step_target: step?.target,
          step_total: activeSteps.length || undefined,
          action: evtAction,
          source: location.pathname === "/walkthrough" ? "walkthrough" : "auto",
        }).catch(() => {});
      } catch { /* swallow — analytics never blocks UX */ }
    };
    if (type === "tour:start") { logEvent("start"); scrollTargetIntoView(step?.target); }
    if (type === "step:before") { logEvent("step_view"); scrollTargetIntoView(step?.target); }
    // After Joyride paints the tooltip, scroll so BOTH the highlighted
    // target and the coaching bubble are inside the viewport together.
    // Uses the combined bounding-box math so we don't have to choose
    // between "show the target" and "show the tooltip" — we fit both.
    if (type === "tooltip") ensureStepFullyVisible();
    if (type === "step:after" && action === "next") logEvent("step_next");
    if (type === "step:after" && action === "prev") logEvent("step_prev");

    // Any exit signal — Finish, Skip, X close button, ESC key, overlay click
    // — stops the tour and marks it seen so it never auto-fires again.
    // Founders can always relaunch from /walkthrough.
    const isExit =
      status === STATUS.FINISHED ||
      status === STATUS.SKIPPED ||
      status === STATUS.PAUSED ||
      status === "finished" ||
      status === "skipped" ||
      action === "close" ||
      action === "skip" ||
      action === "reset" ||
      action === "stop" ||
      origin === "button_close" ||
      origin === "overlay" ||
      type === "tour:end";
    if (isExit) {
      // Fire the summary analytics event with the specific exit reason.
      if (status === STATUS.FINISHED || status === "finished") logEvent("finish");
      else if (action === "skip" || status === STATUS.SKIPPED || status === "skipped") logEvent("skip");
      else logEvent("close");
      setRun(false);
      const isSection = tourKey.startsWith("section:");
      try {
        if (tourKey === "victory") {
          localStorage.setItem(VICTORY_SEEN_KEY, "1");
        } else if (isSection) {
          const sectionId = tourKey.split(":")[1];
          if (status === STATUS.FINISHED || status === "finished") {
            const raw = localStorage.getItem(SECTION_TOURS_DONE_KEY);
            const done = new Set(raw ? JSON.parse(raw) : []);
            done.add(sectionId);
            localStorage.setItem(SECTION_TOURS_DONE_KEY, JSON.stringify(Array.from(done)));
          }
        } else {
          localStorage.setItem(TOUR_SEEN_KEY, "1");
        }
      } catch { /* private mode */ }
      // After the platform menu overview finishes, drop the founder on the
      // Walkthrough page so they can pick a deep-dive section next.
      if (tourKey === "platform" && (status === STATUS.FINISHED || status === "finished") && location.pathname !== "/walkthrough") {
        nav("/walkthrough");
      }
      // After a section tour finishes, return to the walkthrough hub so the
      // founder can pick another section.
      if (isSection && (status === STATUS.FINISHED || status === "finished") && location.pathname !== "/walkthrough") {
        setTimeout(() => nav("/walkthrough"), 250);
      }
    }
  };

  return (
    <Joyride
      steps={activeSteps.map((s) => ({ ...s, skipBeacon: true }))}
      run={run}
      continuous
      showSkipButton
      showProgress
      skipBeacon
      // Joyride v3's step-transition scroll uses `block:"center"` which
      // silently ignores CSS `scroll-padding-top`, so the target lands
      // behind the fixed AppShell header on any step that requires a
      // scroll. We disable Joyride's built-in scroller and take full
      // control via `scrollTargetIntoView` (fired on tour:start and
      // step:before) which uses `block:"start"` — that DOES respect
      // `scroll-padding-top`, so the target always lands with the
      // header's worth of breathing room above it.
      disableScrolling
      spotlightPadding={6}
      onEvent={handleCallback}
      locale={{ back: "Back", close: "Close", last: "Finish", next: "Next", skip: "Skip tour" }}
      styles={{
        options: {
          primaryColor: "#D81B60",          // CPG magenta
          textColor: "#0F172A",
          backgroundColor: "#FFFFFF",
          overlayColor: "rgba(15, 23, 42, 0.55)",
          arrowColor: "#FFFFFF",
          zIndex: 10_000,
        },
        buttonNext: {
          borderRadius: 9999,
          fontWeight: 700,
          fontSize: 12,
          textTransform: "uppercase",
          letterSpacing: "0.14em",
          padding: "10px 18px",
          background: "linear-gradient(135deg, #D81B60 0%, #6A0DAD 100%)",
        },
        buttonBack: {
          color: "#6B7280",
          fontSize: 12,
          fontWeight: 600,
        },
        buttonSkip: {
          color: "#94A3B8",
          fontSize: 11,
        },
        tooltip: {
          borderRadius: 16,
          fontSize: 14,
          padding: 22,
          boxShadow: "0 20px 60px rgba(15,23,42,0.25)",
        },
        tooltipTitle: {
          fontFamily: "'Fraunces', Georgia, serif",
          fontSize: 20,
          lineHeight: 1.15,
          margin: 0,
        },
        tooltipContent: { fontSize: 13.5, lineHeight: 1.55, paddingTop: 8 },
      }}
    />
  );
}
